import { useEffect, useMemo, useState } from "react";
import { Banner, Button } from "@panelgenie/shared-ui";
import type { InvocationDetail } from "../types.js";
import { formatRawResponse } from "../lib/format.js";
import {
  copyTextToClipboard,
  type CopyTextToClipboard,
} from "../lib/clipboard.js";
import {
  breakerPhotoIds as breakerPhotoIdsOf,
  derivedPhotoFor,
} from "../lib/pipeline.js";
import {
  breakerRows,
  filterRows,
  panelIdentityOf,
  sortRows,
  type FilterKey,
  type Sort,
  type SortKey,
} from "../lib/reads.js";
import { InvocationTopBar } from "./InvocationTopBar.js";
import { PhotosRail } from "./PhotosRail.js";
import { StagePane } from "./StagePane.js";
import { ReadsPanel } from "./ReadsPanel.js";
import { JsonViewModal } from "./JsonViewModal.js";
import {
  SaveTestCaseModal,
  type SaveTestCaseSubmit,
} from "./SaveTestCaseModal.js";

/**
 * The invocation-detail surface — a full-viewport, three-pane debugging
 * console (handoff + `SupportConsole-ImagePipeline-Brief.md`). It answers
 * "why did the AI say what it said, and where did it go wrong?" by showing
 * what the model actually read.
 *
 *   ┌ photos rail ┬ stage pane (read image / stage image) ┬ reads panel ┐
 *
 * UR-6 — the union read path reads each breaker photo as one whole-panel
 * image, so the center pane shows that READ image with the geometry/column
 * overlay (or an identity image when one is picked in the rail). The rail
 * carries the photo tracks; the breaker list keeps the right pane. The old
 * per-tile pop-up / trace-to-tile motion is gone — there are no tiles on the
 * union path, and the new scan process has no separate geometry stage cards.
 *
 * The page never scrolls; each pane scrolls internally. State is local
 * (the route fetches the real invocation and hands it down). The model /
 * usage / parse stamp lives in the top bar so a regression is framed
 * before the dive: model problem, parse problem, or image problem.
 */

export type InvocationDetailViewProps = {
  readonly detail: InvocationDetail;
  /**
   * Persist this invocation as a durable regression test case
   * (`support.createTestCase`). Injected so the view is testable without a
   * tRPC client; the route wires the real mutation.
   */
  readonly saveTestCase?: SaveTestCaseSubmit;
  /** Navigate to the new case's detail page on a successful save. */
  readonly onSavedTestCase?: (id: string) => void;
  readonly copyText?: CopyTextToClipboard;
};

export function InvocationDetailView({
  detail,
  saveTestCase,
  onSavedTestCase,
  copyText = copyTextToClipboard,
}: InvocationDetailViewProps) {
  const { invocation, parse, photos, suggestions } = detail;
  const derived = detail.derived ?? null;
  const parseFailed = parse.status !== "ok";

  const breakerPhotoIds = useMemo(() => breakerPhotoIdsOf(derived), [derived]);
  const identity = useMemo(() => panelIdentityOf(suggestions), [suggestions]);
  const allRows = useMemo(
    () => breakerRows(suggestions, identity),
    [suggestions, identity],
  );

  // Default selection: the first read-track (tiled) photo — that's where the
  // diagnostic action is — falling back to the first photo of any kind.
  const initialPhotoId = useMemo(() => {
    const firstBreaker = photos.find((p) => breakerPhotoIds.has(p.photoId));
    return firstBreaker?.photoId ?? photos[0]?.photoId ?? null;
  }, [photos, breakerPhotoIds]);

  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(initialPhotoId);
  const [selectedBreakerId, setSelectedBreakerId] = useState<string | null>(null);
  const [sort, setSort] = useState<Sort>({ key: "circuit", dir: 1 });
  const [filter, setFilter] = useState<FilterKey>("all");

  // Reset the whole interaction state when navigating between invocations.
  // biome-ignore lint/correctness/useExhaustiveDependencies: invocation.id is a deliberate reset trigger — not read in the body, but a change in it is exactly when this effect must re-run.
  useEffect(() => {
    setSelectedPhotoId(initialPhotoId);
    setSelectedBreakerId(null);
    setSort({ key: "circuit", dir: 1 });
    setFilter("all");
  }, [invocation.id, initialPhotoId]);

  const selectedPhoto =
    photos.find((p) => p.photoId === selectedPhotoId) ?? photos[0];
  const isBreaker =
    selectedPhoto !== undefined && breakerPhotoIds.has(selectedPhoto.photoId);
  const derivedPhoto = derivedPhotoFor(derived, selectedPhoto?.photoId ?? null);
  // The selected breaker's box, for the read-image highlight (ReadImageView
  // only draws it when the region's photo is the one on screen).
  const highlightRegion =
    (selectedBreakerId !== null
      ? allRows.find((r) => r.suggestionId === selectedBreakerId)?.region
      : null) ?? null;

  const rows = useMemo(
    () => sortRows(filterRows(allRows, filter), sort),
    [allRows, filter, sort],
  );

  const handleSelectPhoto = (photoId: string) => {
    setSelectedPhotoId(photoId);
  };

  // Selecting a breaker (a reads-table row) highlights it and pulls its
  // source read photo into the center pane (the union read image). There are
  // no tiles on the union path, so there's nothing finer to trace to.
  const handleSelectBreaker = (suggestionId: string) => {
    setSelectedBreakerId(suggestionId);
    const row = allRows.find((r) => r.suggestionId === suggestionId);
    if (row === undefined) return;
    if (
      row.sourcePhotoId !== null &&
      breakerPhotoIds.has(row.sourcePhotoId) &&
      row.sourcePhotoId !== selectedPhotoId
    ) {
      setSelectedPhotoId(row.sourcePhotoId);
    }
  };

  const handleSortChange = (key: SortKey) =>
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 1 ? -1 : 1 } : { key, dir: 1 },
    );

  // Preserved support tooling: curate this case into the regression corpus
  // (TC-6), and copy the verbatim raw response.
  const [jsonOpen, setJsonOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  // A provider error captured no response, so there's no baseline to
  // regress against — the server rejects it and the action is disabled.
  const canSaveTestCase =
    saveTestCase !== undefined && invocation.rawResponse !== null;

  return (
    <div
      aria-label="invocation-detail"
      className="flex h-full min-h-0 flex-col overflow-hidden bg-bg-2"
    >
      <InvocationTopBar
        invocation={invocation}
        parse={parse}
        actions={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setJsonOpen(true)}
              disabled={invocation.rawResponse === null}
              aria-label="view-json"
              title="View the raw model response JSON."
            >
              View JSON
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setSaveOpen(true)}
              disabled={!canSaveTestCase}
              aria-label="save-as-test-case"
              title={
                canSaveTestCase
                  ? "Save this scan as a durable regression test case."
                  : "Nothing to save — this provider error captured no response."
              }
            >
              Save as test case
            </Button>
          </>
        }
      />

      {invocation.errorMessage !== null ? (
        <Banner variant="danger" title="Provider error">
          <span className="font-mono text-sm">{invocation.errorMessage}</span>
        </Banner>
      ) : null}

      <div
        className="grid min-h-0 flex-1"
        style={{ gridTemplateColumns: "280px minmax(0, 1fr) 460px" }}
      >
        <PhotosRail
          photos={photos}
          derived={derived}
          breakerPhotoIds={breakerPhotoIds}
          selectedPhotoId={selectedPhoto?.photoId ?? null}
          onSelectPhoto={handleSelectPhoto}
        />
        <StagePane
          selectedPhoto={selectedPhoto}
          isBreaker={isBreaker}
          derivedPhoto={derivedPhoto}
          parseFailed={parseFailed}
          highlightRegion={highlightRegion}
        />
        <ReadsPanel
          rows={rows}
          totalReads={allRows.length}
          identity={identity}
          invocation={invocation}
          parseFailed={parseFailed}
          selectedBreakerId={selectedBreakerId}
          onSelectBreaker={handleSelectBreaker}
          sort={sort}
          onSortChange={handleSortChange}
          filter={filter}
          onFilterChange={setFilter}
        />
      </div>

      {jsonOpen ? (
        <JsonViewModal
          json={formatRawResponse(invocation.rawResponse)}
          onClose={() => setJsonOpen(false)}
          copyText={copyText}
        />
      ) : null}

      {saveOpen && saveTestCase !== undefined ? (
        <SaveTestCaseModal
          invocationId={invocation.id}
          submit={saveTestCase}
          onSaved={(id) => {
            setSaveOpen(false);
            onSavedTestCase?.(id);
          }}
          onClose={() => setSaveOpen(false)}
        />
      ) : null}
    </div>
  );
}
